"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { actionLogger } from "@/lib/action-logger"
import { toast } from "sonner"
import { useTranslations } from "next-intl"

interface FeedbackModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDescription?: string
  initialGoal?: string
}

export function FeedbackModal({ open, onOpenChange, initialDescription = "", initialGoal = "" }: FeedbackModalProps) {
  const t = useTranslations("feedbackModal")
  const [description, setDescription] = useState(initialDescription)
  const [goal, setGoal] = useState(initialGoal)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setDescription(initialDescription)
      setGoal(initialGoal)
    }
  }, [open, initialDescription, initialGoal])

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error(t("pleaseDescribeWhatHappened"))
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          description: description.trim(),
          goal: goal.trim() || null,
          recentActions: actionLogger.getRecentActions(),
          url: window.location.href,
          browser: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      })

      if (!response.ok) {
        throw new Error(t("failedToSubmitFeedback"))
      }

      toast.success(t("feedbackSubmittedThankYou"))
      setDescription("")
      setGoal("")
      onOpenChange(false)
    } catch (error) {
      console.error("Failed to submit feedback:", error)
      toast.error(t("failedToSubmitFeedbackPleaseTryAgain"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("sendFeedback")}</DialogTitle>
          <DialogDescription>
            {t("helpUsImproveByDescribingWhatHappenedWe")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="description">{t("whatHappened")}</Label>
            <Textarea
              id="description"
              placeholder={t("exampleClickedSearchResultsLoadedClick")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="goal">{t("whatWereYouTryingToDoOptional")}</Label>
            <Input
              id="goal"
              placeholder={t("exampleFindImagesWithSpecificTags")}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t("cancel")}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? t("submitting") : t("submitFeedback")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
